import { useCallback, useEffect, useRef, useState } from "react";
import type { InputNumberValueChangeEvent } from "primereact/inputnumber";

export const useDebouncedNumberValueChange = ({
    value,
    onValueChange,
    debounceMs,
}: {
    value?: number | null;
    onValueChange?: (event: InputNumberValueChangeEvent) => void;
    debounceMs: number;
}) => {
    const isDebounced = debounceMs > 0;
    const [localValue, setLocalValue] = useState<number | null | undefined>(value);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const onValueChangeRef = useRef(onValueChange);
    onValueChangeRef.current = onValueChange;

    useEffect(() => {
        if (!isDebounced) {
            return;
        }
        setLocalValue(value);
    }, [isDebounced, value]);

    useEffect(
        () => () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        },
        [],
    );

    const emitChange = useCallback((nextValue: number | null) => {
        onValueChangeRef.current?.({
            value: nextValue,
            originalEvent: undefined,
        } as InputNumberValueChangeEvent);
    }, []);

    const flushPendingChange = useCallback(
        (nextValue: number | null) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = undefined;
            }
            emitChange(nextValue);
        },
        [emitChange],
    );

    const handleValueChange = useCallback(
        (event: InputNumberValueChangeEvent) => {
            const nextValue = event.value ?? null;
            if (!isDebounced) {
                onValueChangeRef.current?.(event);
                return;
            }

            setLocalValue(nextValue);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                timerRef.current = undefined;
                emitChange(nextValue);
            }, debounceMs);
        },
        [debounceMs, emitChange, isDebounced],
    );

    const handleBlurFlush = useCallback(
        (nextValue: number | null) => {
            if (!isDebounced) {
                return;
            }
            flushPendingChange(nextValue);
            setLocalValue(nextValue);
        },
        [flushPendingChange, isDebounced],
    );

    return {
        displayValue: isDebounced ? localValue : value,
        handleValueChange,
        handleBlurFlush,
        isDebounced,
    };
};
